<script setup lang="ts">
import ValidatedInput from "@/builder/components/input/ValidatedInput.vue";
import TestPayments from "@/builder/components/payment/TestPayments.vue";
import { useRenderStore } from '@/builder/stores/renderStore';
import { MerchantType } from '@/enums';
import { PropType, Ref, computed, inject } from "vue";
import { PageDesign } from '../page/PageInterface';
import AuthNetPaymentComponent from './AuthNetPaymentComponent.vue';
import { IPaymentComponent } from './PaymentInterface';
import PaypalPaymentComponent from './PaypalPaymentComponent.vue';
import StripePaymentComponent from './StripePaymentComponent.vue';

// injects
const renderStore = useRenderStore();
const pageDesign = inject("pageDesign") as Ref<PageDesign>;

// props
const props = defineProps({
    component: {
        type: Object as PropType<IPaymentComponent>,
        required: true
    },
    organizationId: {
        type: Number as PropType<number | null>,
        required: true,
        default: null
    },
    merchantAccount: {
        type: String,
        required: true,
    },
    tokenizationKey: {
        type: String,
        required: true,
    },
    readOnly: {
        type: Boolean,
        required: true,
        default: false
    },
    payPalGatewayId: {
        type: Number as PropType<number | null>,
        required: false,
        default: null
    },
    payPalMerchantAccount: {
        type: String as PropType<string | null>,
        required: false,
        default: null
    },
    payPalCurrency: {
        type: String as PropType<string | null>,
        required: false,
        default: null
    },
    merchantType: {
        type: Number as PropType<MerchantType>,
        required: true,
    }
});

// computed
const parentStep = computed(() => {
    return renderStore.getFormStep(props.component.id);
});

const formIsOnParentStep = computed(() => {
    return renderStore.step === parentStep.value;
});

const hasPayPal = computed(() => {
    return (
        props.component.customData.enablePayPal &&
        props.payPalMerchantAccount !== null &&
        props.payPalCurrency !== null &&
        props.payPalGatewayId !== null
    );
});

</script>

<template>
    <section class="payment-container">
        <template v-if="renderStore.isTestMode">
            <TestPayments />
        </template>

        <form :id="component.id + '_payment-element-form'">
            <PaypalPaymentComponent v-if="hasPayPal" :component="props.component" :readOnly="props.readOnly"
                :payPalGatewayId="props.payPalGatewayId" :payPalMerchantAccount="props.payPalMerchantAccount"
                :payPalCurrency="props.payPalCurrency" :formIsOnParentStep="formIsOnParentStep"
                :pageDesign="pageDesign" />

            <StripePaymentComponent v-if="props.merchantType == MerchantType.StripeUnified" :component="props.component"
                :readOnly="props.readOnly" :merchantAccount="props.merchantAccount"
                :tokenizationKey="props.tokenizationKey" :formIsOnParentStep="formIsOnParentStep"
                :pageDesign="pageDesign" :organizationId="props.organizationId" />

            <AuthNetPaymentComponent v-if="props.merchantType == MerchantType.AuthorizeNet" :component="props.component"
                :readOnly="props.readOnly" :merchantAccount="props.merchantAccount"
                :tokenizationKey="props.tokenizationKey" :formIsOnParentStep="formIsOnParentStep"
                :pageDesign="pageDesign" :organizationId="props.organizationId" />
        </form>

        <ValidatedInput :id="component.id" :displayName="'Payment Method Type'"
            :value="renderStore.gift.paymentMethodType" :isStepError="true" name="PaymentMethodType">
        </ValidatedInput>

        <ValidatedInput :id="component.id" :displayName="'Payment Method'" :value="renderStore.gift.paymentMethodId"
            :isRequired="true" :isStepError="true" name="PaymentMethodId">
        </ValidatedInput>
    </section>
</template>
